/* ==============================================================================
   Permissões
   ==============================================================================
   Bitfield de 64 bits. Implementado UMA VEZ aqui e usado por servidor
   (autorização) e cliente (esconder UI).

   O cliente calcular permissão é otimização de UX. **O servidor sempre
   recalcula.** Nada é autorizado por confiar no cliente.

   Por que BigInt e não number
   ---------------------------
   `number` em JS é IEEE 754 de precisão dupla: inteiro exato só até 2^53−1.
   Acima disso, `2**53 + 1 === 2**53` é `true`. Como há bits acima de 53
   (ADMINISTRATOR é 1n << 40n, mas MANAGE_INSTANCE é 1n << 56n), usar `number`
   faria permissões altas serem silenciosamente perdidas em operações de bit.

   Permissão silenciosamente errada é falha de segurança, não bug de UI. Daí
   BigInt em todo o caminho, e `bigint` na coluna do Postgres.
   ============================================================================== */

/**
 * Cada permissão é um bit. Os grupos deixam faixas livres entre si para que
 * adicionar uma permissão nova de texto não exija realocar as de voz — o valor
 * numérico é persistido no banco e mudá-lo quebraria dados existentes.
 */
export const Permission = {
  // --- Texto: bits 0-9 ---
  VIEW_CHANNEL:        1n << 0n,
  SEND_MESSAGES:       1n << 1n,
  MANAGE_MESSAGES:     1n << 2n,
  ATTACH_FILES:        1n << 3n,
  ADD_REACTIONS:       1n << 4n,
  MENTION_EVERYONE:    1n << 5n,
  EMBED_LINKS:         1n << 6n,
  READ_MESSAGE_HISTORY: 1n << 7n,
  USE_EXTERNAL_EMOJIS: 1n << 8n,

  // --- Voz e mídia: bits 10-19 ---
  CONNECT:             1n << 10n,
  SPEAK:               1n << 11n,
  VIDEO:               1n << 12n,
  SHARE_SCREEN:        1n << 13n,
  /**
   * Separada de SHARE_SCREEN de propósito: é a permissão que custa banda.
   * Em mesh, 2 telas para 7 pares são 14 fluxos de upload. Poder limitar
   * multi-tela por papel é controle operacional real, não granularidade
   * decorativa. Ver docs/ARCHITECTURE.md §9.
   */
  SHARE_MULTI_SCREEN:  1n << 14n,
  MUTE_MEMBERS:        1n << 15n,
  DEAFEN_MEMBERS:      1n << 16n,
  MOVE_MEMBERS:        1n << 17n,
  PRIORITY_SPEAKER:    1n << 18n,

  // --- Gestão: bits 20-39 ---
  MANAGE_CHANNELS:     1n << 20n,
  MANAGE_ROLES:        1n << 21n,
  MANAGE_GUILD:        1n << 22n,
  MANAGE_EMOJIS:       1n << 23n,
  MANAGE_WEBHOOKS:     1n << 24n,
  MANAGE_NICKNAMES:    1n << 25n,
  CHANGE_NICKNAME:     1n << 26n,
  KICK_MEMBERS:        1n << 27n,
  BAN_MEMBERS:         1n << 28n,
  MODERATE_MEMBERS:    1n << 29n,
  VIEW_AUDIT_LOG:      1n << 30n,
  CREATE_INVITE:       1n << 31n,

  // --- Especiais: bits 40+ ---
  /** Concede tudo dentro da guild. Curto-circuita a resolução. */
  ADMINISTRATOR:       1n << 40n,
  /** Administração da instância inteira, fora do modelo de guild. */
  MANAGE_INSTANCE:     1n << 56n,
} as const;

export type PermissionName = keyof typeof Permission;

/** Todos os bits definidos. É o que ADMINISTRATOR e o dono da guild recebem. */
export const ALL_PERMISSIONS: bigint = Object.values(Permission).reduce(
  (acc, bit) => acc | bit,
  0n,
);

/**
 * Padrão do papel @everyone numa guild nova: participar, sem gerenciar nada.
 *
 * SHARE_MULTI_SCREEN fica FORA de propósito — multi-tela é a permissão de maior
 * custo de banda e deve ser concedida deliberadamente, não herdada.
 */
export const DEFAULT_EVERYONE_PERMISSIONS: bigint =
  Permission.VIEW_CHANNEL |
  Permission.SEND_MESSAGES |
  Permission.ATTACH_FILES |
  Permission.ADD_REACTIONS |
  Permission.EMBED_LINKS |
  Permission.READ_MESSAGE_HISTORY |
  Permission.USE_EXTERNAL_EMOJIS |
  Permission.CONNECT |
  Permission.SPEAK |
  Permission.VIDEO |
  Permission.SHARE_SCREEN |
  Permission.CHANGE_NICKNAME |
  Permission.CREATE_INVITE;

/* ------------------------------------------------------------------------------
   Operações de bit
------------------------------------------------------------------------------ */

/** `true` se TODOS os bits de `required` estiverem presentes. */
export function has(permissions: bigint, required: bigint): boolean {
  return (permissions & required) === required;
}

/** `true` se ALGUM dos bits de `any` estiver presente. */
export function hasAny(permissions: bigint, any: bigint): boolean {
  return (permissions & any) !== 0n;
}

export function add(permissions: bigint, bits: bigint): bigint {
  return permissions | bits;
}

export function remove(permissions: bigint, bits: bigint): bigint {
  return permissions & ~bits;
}

/** Nomes das permissões presentes. Para UI e para a trilha de auditoria. */
export function toNames(permissions: bigint): PermissionName[] {
  return (Object.keys(Permission) as PermissionName[]).filter((name) =>
    has(permissions, Permission[name]),
  );
}

export function fromNames(names: readonly PermissionName[]): bigint {
  return names.reduce((acc, name) => acc | Permission[name], 0n);
}

/**
 * Converte o valor vindo do banco.
 *
 * O driver `pg` entrega `bigint` como STRING por default, justamente para não
 * perder precisão. Aceitar string aqui evita um `Number()` acidental no meio
 * do caminho, que é exatamente o bug que o uso de BigInt existe para prevenir.
 */
export function parsePermissions(value: string | number | bigint | null | undefined): bigint {
  if (value === null || value === undefined) return 0n;
  if (typeof value === 'bigint') return value;
  if (typeof value === 'number') {
    if (!Number.isSafeInteger(value)) {
      throw new RangeError(
        `Permissão ${value} excede o inteiro seguro de JS e já perdeu precisão. ` +
          'Passe string ou bigint, nunca number.',
      );
    }
    return BigInt(value);
  }
  const trimmed = value.trim();
  if (!/^-?\d+$/.test(trimmed)) {
    throw new TypeError(`Permissão inválida: ${JSON.stringify(value)}`);
  }
  return BigInt(trimmed);
}

/* ------------------------------------------------------------------------------
   Resolução
------------------------------------------------------------------------------ */

export interface RoleLike {
  id: string;
  permissions: bigint;
  /** Maior = mais alto. Usado na checagem de hierarquia. */
  position: number;
  isEveryone?: boolean;
}

export interface OverwriteLike {
  targetType: 'role' | 'member';
  targetId: string;
  allow: bigint;
  deny: bigint;
}

export interface ResolveContext {
  guildOwnerId: string;
  guildDefaultPermissions: bigint;
  userId: string;
  /** Papéis do membro, incluindo @everyone. */
  roles: readonly RoleLike[];
  /** Overwrites do canal. Vazio resolve só as permissões da guild. */
  overwrites?: readonly OverwriteLike[];
}

/**
 * Permissões do membro na guild, ignorando canal.
 *
 * Ordem: default da guild, OR de todos os papéis, e curto-circuito de
 * ADMINISTRATOR e de dono.
 */
export function resolveGuildPermissions(ctx: ResolveContext): bigint {
  // O dono não pode ser trancado fora da própria guild por configuração errada.
  if (ctx.userId === ctx.guildOwnerId) return ALL_PERMISSIONS;

  let permissions = ctx.guildDefaultPermissions;
  for (const role of ctx.roles) {
    permissions |= role.permissions;
  }

  if (has(permissions, Permission.ADMINISTRATOR)) return ALL_PERMISSIONS;
  return permissions;
}

/**
 * Permissões efetivas do membro num canal.
 *
 * A ordem é fixa e a razão de cada passo importa:
 *
 *   1. permissões da guild (com curto-circuito de admin/dono)
 *   2. overwrite do @everyone no canal
 *   3. overwrites dos papéis do membro — TODOS os denies acumulados primeiro,
 *      depois TODOS os allows. Isso faz um allow em qualquer papel vencer um
 *      deny em outro papel, que é o comportamento esperado de "este papel
 *      libera".
 *   4. overwrite específico do membro — vence os de papel, porque "exceção
 *      para uma pessoa" precisa vencer regra de grupo.
 *
 * Se os passos 3 e 4 fossem aplicados na mesma passada, um deny de papel de
 * posição alta bloquearia um allow de papel de posição baixa de forma
 * dependente de ordem — não determinística e impossível de explicar ao usuário.
 */
export function resolveChannelPermissions(ctx: ResolveContext): bigint {
  const base = resolveGuildPermissions(ctx);

  // Admin e dono ignoram overwrites. Reavaliar aqui evita que um deny de canal
  // tranque um administrador fora.
  if (base === ALL_PERMISSIONS) return ALL_PERMISSIONS;

  const overwrites = ctx.overwrites ?? [];
  if (overwrites.length === 0) return base;

  let permissions = base;

  // 2. @everyone
  const everyoneRole = ctx.roles.find((r) => r.isEveryone);
  if (everyoneRole) {
    const ow = overwrites.find(
      (o) => o.targetType === 'role' && o.targetId === everyoneRole.id,
    );
    if (ow) {
      permissions = (permissions & ~ow.deny) | ow.allow;
    }
  }

  // 3. papéis do membro: denies primeiro, allows depois
  let roleAllow = 0n;
  let roleDeny = 0n;
  for (const role of ctx.roles) {
    if (role.isEveryone) continue; // já aplicado no passo 2
    const ow = overwrites.find(
      (o) => o.targetType === 'role' && o.targetId === role.id,
    );
    if (ow) {
      roleAllow |= ow.allow;
      roleDeny |= ow.deny;
    }
  }
  permissions = (permissions & ~roleDeny) | roleAllow;

  // 4. membro
  const memberOw = overwrites.find(
    (o) => o.targetType === 'member' && o.targetId === ctx.userId,
  );
  if (memberOw) {
    permissions = (permissions & ~memberOw.deny) | memberOw.allow;
  }

  return permissions;
}

/**
 * Permissões implicadas por outras. Aplicado DEPOIS da resolução.
 *
 * Sem isto, um papel com SEND_MESSAGES e sem VIEW_CHANNEL produziria o estado
 * absurdo de "pode escrever num canal que não vê". A dependência é aplicada
 * como remoção, não como concessão: faltando o pré-requisito, a permissão
 * dependente cai — nunca o contrário, que seria escalação de privilégio.
 */
export function applyImplications(permissions: bigint): bigint {
  let result = permissions;

  // Sem ver o canal, nada mais nele se aplica.
  if (!has(result, Permission.VIEW_CHANNEL)) {
    result = remove(
      result,
      Permission.SEND_MESSAGES |
        Permission.ATTACH_FILES |
        Permission.ADD_REACTIONS |
        Permission.EMBED_LINKS |
        Permission.READ_MESSAGE_HISTORY |
        Permission.MENTION_EVERYONE |
        Permission.MANAGE_MESSAGES,
    );
  }

  // Sem conectar, nenhuma capacidade de mídia faz sentido.
  if (!has(result, Permission.CONNECT)) {
    result = remove(
      result,
      Permission.SPEAK |
        Permission.VIDEO |
        Permission.SHARE_SCREEN |
        Permission.SHARE_MULTI_SCREEN |
        Permission.PRIORITY_SPEAKER,
    );
  }

  // Multi-tela sem poder compartilhar uma é contradição.
  if (!has(result, Permission.SHARE_SCREEN)) {
    result = remove(result, Permission.SHARE_MULTI_SCREEN);
  }

  return result;
}

/** Resolução completa: canal + implicações. É o que a autorização deve usar. */
export function resolveEffectivePermissions(ctx: ResolveContext): bigint {
  return applyImplications(resolveChannelPermissions(ctx));
}

/* ------------------------------------------------------------------------------
   Hierarquia
------------------------------------------------------------------------------ */

/** Posição do papel mais alto do membro. Sem papéis, −1. */
export function highestRolePosition(roles: readonly RoleLike[]): number {
  return roles.reduce((max, r) => (r.position > max ? r.position : max), -1);
}

/**
 * `true` se o ator pode modificar/atribuir o papel alvo.
 *
 * Exige posição ESTRITAMENTE maior. Permitir igual deixaria dois moderadores de
 * mesmo nível se rebaixarem mutuamente — escalada lateral.
 */
export function canManageRole(
  actorRoles: readonly RoleLike[],
  targetRole: RoleLike,
  options: { isOwner?: boolean } = {},
): boolean {
  if (options.isOwner) return true;
  if (targetRole.isEveryone) return false; // @everyone não é gerenciável
  return highestRolePosition(actorRoles) > targetRole.position;
}

/**
 * `true` se o ator pode agir sobre outro membro (kick, ban, mudar apelido).
 *
 * O dono é intocável, e ninguém age sobre si mesmo por esta via.
 */
export function canManageMember(
  actorRoles: readonly RoleLike[],
  targetRoles: readonly RoleLike[],
  options: { actorIsOwner?: boolean; targetIsOwner?: boolean } = {},
): boolean {
  if (options.targetIsOwner) return false;
  if (options.actorIsOwner) return true;
  return highestRolePosition(actorRoles) > highestRolePosition(targetRoles);
}
